import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { useHome, useRequiredIntakes, useRenewalCheckins } from "@/lib/api/hooks";
import { buildHomeTasks } from "@/features/home/homeTasks";
import { TaskList } from "@/features/home/components/TaskList";
import type { HomeTask } from "@/types/bootstrap";
import { cn } from "@/lib/cn";

/**
 * IntakeAttentionDrawer — a once-per-session, bottom-anchored drawer that
 * surfaces the patient's outstanding tasks (intakes, ID upload, etc.) the
 * moment they land, so required work can't be missed. Same feel as the
 * overflow menu and the intake wizard: a blurred full-screen backdrop with
 * the card animating up like a drawer, then animating back out on a task
 * tap or the X.
 *
 * Renders the identical Home task list (via buildHomeTasks + TaskList) so it
 * never disagrees with the Tasks section. Session-scoped (sessionStorage) so
 * it appears once per visit.
 */

const SHOWN_KEY = "intake_attention_shown";
// Matches the drawer's transition-transform duration (duration-base) so the
// slide-down finishes before we unmount.
const EXIT_MS = 220;

// DEV-only preview task so the drawer can be seen without seeded data.
// Stripped from production builds via `import.meta.env.DEV`.
const DEV_PLACEHOLDER: HomeTask = {
  id: "dev-intake-preview",
  kind: "treatment_intake",
  title: "Finish your Sermorelin intake",
  body: "Your clinician needs your medical questionnaire before they can write your script.",
  cta_label: "Finish intake",
  cta_href: "/app/intake",
  tone: null,
};

export function IntakeAttentionDrawer({
  suppressed = false,
}: {
  /** Hold the popup back (e.g. while the guided tour / upsell owns the screen). */
  suppressed?: boolean;
}) {
  const { data: home } = useHome();
  const { data: requiredIntakes } = useRequiredIntakes();
  const { data: renewalCheckins } = useRenewalCheckins();
  const pending = (requiredIntakes ?? []).filter(
    (i) => !i.intake_submitted_at,
  );
  const realTasks = buildHomeTasks(home?.tasks ?? [], pending, renewalCheckins?.items);
  const tasks =
    import.meta.env.DEV && realTasks.length === 0
      ? [DEV_PLACEHOLDER]
      : realTasks;

  // `mounted` keeps the drawer in the DOM; `shown` drives the slide/fade so
  // we get an enter (mount → next frame) and an exit (shown=false → unmount).
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (mounted) return;
    if (suppressed) return;
    if (tasks.length === 0) return;
    if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
    setMounted(true);
  }, [mounted, suppressed, tasks.length]);

  // Enter on the frame after mounting. This lives apart from the effect above: when it sat
  // there, the `mounted` change re-ran that effect, its cleanup cancelled this frame, and the
  // drawer stayed mounted but never shown, leaving an invisible backdrop over the page.
  useEffect(() => {
    if (!mounted) return;
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [mounted]);

  function dismiss() {
    sessionStorage.setItem(SHOWN_KEY, "1");
    setShown(false);
    window.setTimeout(() => setMounted(false), EXIT_MS);
  }

  if (!mounted) return null;

  return (
    <>
      {/* Blurred backdrop — tap to dismiss. It takes taps only while visible, so it can
          never sit invisibly over the page during the enter or exit. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className={cn(
          "fixed inset-0 z-dialog bg-black/10 backdrop-blur-[10px]",
          "transition-opacity duration-base ease-ease",
          shown ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
      />

      {/* Drawer — fixed to the bottom, slides up over the backdrop. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Needs attention"
        className={cn(
          "fixed inset-x-0 bottom-0 z-dialog",
          "transition-transform duration-base ease-ease",
          shown ? "translate-y-0" : "translate-y-full",
        )}
      >
        <div className="max-w-[50rem] mx-auto w-full bg-[var(--surface-page)] shadow-modal flex flex-col max-h-[85vh]">
          {/* Header — mono caps label + X, same layout as the menu/intake
              modals. */}
          <header className="shrink-0 flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle">
            <h2 className="font-mono uppercase text-13 text-ink-secondary px-15 py-15">
              Needs Attention
            </h2>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="w-45 h-45 shrink-0 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </header>

          {/* Full Home task list — tapping a task dismisses as we navigate. */}
          <div className="min-h-0 overflow-y-auto">
            <TaskList tasks={tasks} onTaskClick={dismiss} emphasizeCta />
          </div>
        </div>
      </div>
    </>
  );
}
